// src/components/filters/Filters.js
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ArrowImg from "../../../Assets/images/icons/arrow-down.png";
import MostSearched from "./MostSearched";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import CountryFilter from "./countryFilter";

const CustomArrowSVG = () => (
    <img
        src={ArrowImg}
        alt="arrow"
        style={{ width: "14px", height: "14px", transform: "rotate(0deg)" }}
    />
);

const Filters = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [subcategoryOpen, setSubcategoryOpen] = useState(false);

    // Destructure all props
    const {
        mostsearched,
        sortOrder,
        selectedFeature,
        toggleFeatureFilter,
        valueMap,
        marks,
        handleChange,
        value,
        Slider: SliderComponent,
        selectedFilters,
        toggleFilter,
        handleCountryChange,
        sortedOptions,
        setSelectedSubcategory,
        getSubCategories,
        setSelectedCategories,
        selectedCategories,
        categories,
        handleSearchClick,
        maxPrice,
        minPrice,
        selectedSubcategory,
        selectedCountry,
        searchTerm,
        isLoading,
        handleKeyDown,
        setSearchTerm,
        handleSortByPrice,
    } = props;

    const [most_searched_change, setMost_searched_change] = useState("");

    useEffect(() => {
        if (
            selectedCategories !== "" ||
            selectedSubcategory.length !== 0 ||
            selectedCountry.length !== 0 ||
            selectedFilters.length > 0 ||
            selectedFeature.length !== 0 ||
            maxPrice !== undefined ||
            minPrice !== undefined ||
            sortOrder !== "" ||
            most_searched_change !== ""
        ) {
            handleSearchClick();
        }
    }, [
        selectedCategories,
        selectedSubcategory,
        selectedCountry,
        selectedFilters,
        selectedFeature,
        minPrice,
        maxPrice,
        sortOrder,
        most_searched_change,
    ]);

    const openFilterDialog = () => {
        setCategoryOpen(false);
        setSubcategoryOpen(false);
        setFilterDialogOpen(true);
    };

    const closeFilterDialog = () => {
        setFilterDialogOpen(false);
    };

    // Filter Content Component
    const FilterContent = () => (
        <Grid container spacing={2}>
            {/* Category & Subcategory */}
            <Grid item xs={12} md={6}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Category
                    </Typography>
                    <Autocomplete
                        options={categories}
                        getOptionLabel={(option) => option.label || ""}
                        popupIcon={<CustomArrowSVG />}
                        value={categories.find((cat) => cat.id === selectedCategories) || null}
                        onChange={(event, newValue) => {
                            setSelectedCategories(newValue ? newValue.id : "");
                            setSelectedSubcategory([]);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select category"
                                variant="outlined"
                                sx={textFieldStyle}
                            />
                        )}
                        sx={autocompleteStyle}
                    />
                </Box>
            </Grid>

            <Grid item xs={12} md={6}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Subcategories
                    </Typography>
                    <Autocomplete
                        multiple
                        options={getSubCategories()}
                        disableCloseOnSelect
                        value={getSubCategories().filter((sub) => selectedSubcategory.includes(sub.id))}
                        getOptionLabel={(option) => option.label}
                        popupIcon={<CustomArrowSVG />}
                        onChange={(event, newValues) => {
                            setSelectedSubcategory(newValues.map((sub) => sub.id));
                        }}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id} style={optionItemStyle}>
                                <Checkbox
                                    checked={selected}
                                    sx={checkboxStyle}
                                />
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select subcategories"
                                variant="outlined"
                                sx={textFieldStyle}
                            />
                        )}
                        sx={autocompleteStyle}
                    />
                </Box>
            </Grid>

            {/* Country Filter */}
            <Grid item xs={12}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Countries
                    </Typography>
                    <CountryFilter
                        sortedOptions={sortedOptions}
                        handleCountryChange={handleCountryChange}
                        value={selectedCountry}
                        sx={{ mt: 1 }}
                    />
                </Box>
            </Grid>

            {/* Popular Filters */}
            <Grid item xs={12}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Popular
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {["Provider", "Reseller", "Trust", "Old Domain"].map((filter) => (
                            <FilterButton
                                key={filter}
                                label={filter}
                                active={selectedFilters.includes(filter.toLowerCase())}
                                onClick={() => toggleFilter(filter.toLowerCase())}
                            />
                        ))}
                    </Box>
                </Box>
            </Grid>

            {/* Price Slider */}
            <Grid item xs={12}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Price Range
                    </Typography>
                    <SliderComponent
                        value={value}
                        onChange={handleChange}
                        step={null}
                        marks={marks}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(val) => valueMap[val]}
                        disableSwap
                        sx={sliderStyle}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -2 }}>
                        {marks.slice(0, 5).map((mark, index) => (
                            <Typography key={index} variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {valueMap[mark.value]}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Grid>

            {/* Features */}
            <Grid item xs={12}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Features
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {["Refill", "New Services", "Dripfeed", "Cancel"].map((feature) => (
                            <FilterButton
                                key={feature}
                                label={feature}
                                active={selectedFeature.includes(feature)}
                                onClick={() => toggleFeatureFilter(feature)}
                            />
                        ))}
                    </Box>
                </Box>
            </Grid>

            {/* Sort by Price */}
            <Grid item xs={12}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Sort by Price
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {[
                            { label: "Low to High", value: "asc" },
                            { label: "High to Low", value: "desc" },
                        ].map((option) => (
                            <FilterButton
                                key={option.value}
                                label={option.label}
                                active={sortOrder === option.value}
                                onClick={() => handleSortByPrice(option.value)}
                            />
                        ))}
                    </Box>
                </Box>
            </Grid>

            {/* Most Searched */}
            <Grid item xs={12}>
                <Box sx={filterSectionStyle}>
                    <Typography variant="subtitle1" sx={sectionTitleStyle}>
                        Most Searched
                    </Typography>
                    <MostSearched
                        mostsearched={mostsearched}
                        setSearchTerm={setSearchTerm}
                        searchTerm={searchTerm}
                        setMost_searched_change={setMost_searched_change}
                        sx={{ mt: 1 }}
                    />
                </Box>
            </Grid>
        </Grid>
    );

    return (
        <Box sx={{
            padding: { xs: "15px", sm: "20px" },
            marginTop: "20px",
            background: "#c2ddd1",
            borderRadius: "12px",
        }}>
            {/* FIRST ROW: Search Bar and Search Button */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexDirection: { xs: "column", sm: "row" }
            }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type here what you are looking for (Services or Panels)"
                    sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: "8px",
                            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                        },
                        "& .MuiInputBase-input": {
                            padding: "12px 16px",
                            color: "#333",
                            fontSize: "16px",
                        },
                    }}
                />

                <Button
                    variant="contained"
                    color="primary"
                    disabled={
                        isLoading ||
                        !(
                            searchTerm.trim() !== "" ||
                            selectedCountry.length > 0 ||
                            selectedCategories ||
                            selectedSubcategory.length > 0 ||
                            selectedFilters.length > 0 ||
                            selectedFeature.length > 0 ||
                            minPrice ||
                            maxPrice
                        )
                    }
                    onClick={handleSearchClick}
                    sx={{
                        flex: { xs: 1, sm: "none" },
                        background: "#979797",
                        padding: "8px 20px",
                        borderRadius: "8px",
                        fontSize: "15px",
                        fontWeight: 500,
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
                        minWidth: "110px",
                        width: { xs: "100%", sm: "auto" },
                        mt: { xs: 1.5, sm: 0 },
                        "&:hover": {
                            background: "#4ea27d"
                        }
                    }}
                >
                    Search
                </Button>
            </Box>

            {/* SECOND ROW: Category, Subcategory and Filter Button */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* Category Filter */}
                <Grid item xs={12} sm={5} md={5}>
                    <Autocomplete
                        open={categoryOpen}
                        onOpen={() => setCategoryOpen(true)}
                        onClose={() => setCategoryOpen(false)}
                        options={categories}
                        getOptionLabel={(option) => option.label || ""}
                        popupIcon={<CustomArrowSVG />}
                        value={categories.find((cat) => cat.id === selectedCategories) || null}
                        onChange={(event, newValue) => {
                            setSelectedCategories(newValue ? newValue.id : "");
                            setSelectedSubcategory([]);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select category"
                                variant="outlined"
                                sx={{
                                    ...textFieldStyle,
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        )}
                        sx={{
                            ...autocompleteStyle,
                            width: "100%",
                        }}
                    />
                </Grid>

                {/* Subcategory Filter */}
                <Grid item xs={12} sm={5} md={5}>
                    <Autocomplete
                        open={subcategoryOpen}
                        onOpen={() => setSubcategoryOpen(true)}
                        onClose={() => setSubcategoryOpen(false)}
                        multiple
                        options={getSubCategories()}
                        disableCloseOnSelect
                        value={getSubCategories().filter((sub) => selectedSubcategory.includes(sub.id))}
                        getOptionLabel={(option) => option.label}
                        popupIcon={<CustomArrowSVG />}
                        onChange={(event, newValues) => {
                            setSelectedSubcategory(newValues.map((sub) => sub.id));
                        }}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id} style={optionItemStyle}>
                                <Checkbox
                                    checked={selected}
                                    sx={checkboxStyle}
                                />
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Select subcategories"
                                variant="outlined"
                                sx={{
                                    ...textFieldStyle,
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        )}
                        sx={{
                            ...autocompleteStyle,
                            width: "100%",
                        }}
                    />
                </Grid>

                {/* Filter Button */}
                <Grid item xs={12} sm={2} md={2}
                      sx={{
                          display: "flex",
                          justifyContent: { xs: "flex-start", sm: "flex-end" },
                          alignItems: "center",
                          mt: { xs: 0, sm: 0 }
                      }}>
                    <Button
                        variant="outlined"
                        onClick={openFilterDialog}
                        startIcon={<FilterListIcon />}
                        sx={{
                            borderColor: "#979797",
                            color: "#979797",
                            backgroundColor: "#FFF",
                            "&:hover": {
                                borderColor: "#979797",
                                backgroundColor: "rgba(196, 196, 196, 0.1)"
                            },
                            minWidth: "110px",
                            fontWeight: 500,
                            fontSize: "15px",
                            py: 1,
                            width: { xs: "100%", sm: "auto",md:"100%" },
                            height: "51px",
                        }}
                    >
                        Filters
                    </Button>
                </Grid>
            </Grid>

            {/* Filter Dialog */}
            <Dialog
                open={filterDialogOpen}
                onClose={closeFilterDialog}
                fullScreen={isMobile}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : "12px",
                        backgroundColor: "#c2ddd1",
                        backgroundImage: "none",
                        maxHeight: "90vh",
                        overflowY: 'auto'
                    }
                }}
            >
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#2C3E50" }}>
                            Filter Options
                        </Typography>
                        <IconButton onClick={closeFilterDialog} sx={{ color: "#2C3E50" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <FilterContent />

                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        mt: 4,
                        pt: 3,
                        borderTop: "1px solid #c2ddd1",
                    }}>
                        <Button
                            variant="outlined"
                            onClick={closeFilterDialog}
                            sx={{
                                flex: 1,
                                borderColor: "#4ea27d",
                                color: "#4ea27d",
                                fontWeight: 500,
                                py: 1.5,
                                "&:hover": {
                                    borderColor: "#979797",
                                    backgroundColor: "rgba(151, 151, 151, 0.08)"
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                handleSearchClick();
                                closeFilterDialog();
                            }}
                            sx={{
                                flex: 1,
                                background: "#979797",
                                fontWeight: 500,
                                py: 1.5,
                                "&:hover": {
                                    background: "#4ea27d"
                                }
                            }}
                        >
                            Apply Filters
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

// Reusable Filter Button Component
const FilterButton = ({ label, active, onClick }) => (
    <Button
        variant={active ? "contained" : "outlined"}
        onClick={onClick}
        sx={{
            borderRadius: "6px",
            backgroundColor: active ? "#4ea27d" : "transparent",
            color: active ? "#FFF" : "#474747",
            border: `1px solid ${active ? "#979797" : "#A7A7A7"}`,
            textTransform: "none",
            fontWeight: 400,
            fontSize: "14px",
            padding: "6px 12px",
            boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
            whiteSpace: 'nowrap',
            "&:hover": {
                backgroundColor: active ? "#4ea27d" : "#f0f0f0",
                borderColor: active ? "#4ea27d" : "#8a8a8a"
            }
        }}
    >
        {label}
    </Button>
);

// Styles
const filterSectionStyle = {
    background: "#FFF",
    padding: "16px",
    borderRadius: "8px",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
};

const sectionTitleStyle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2C3E50",
    mb: 1.5,
};

const textFieldStyle = {
    backgroundColor: '#F5F8FE',
    borderRadius: '8px',
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            border: "1px solid #c2ddd1",
            borderRadius: "8px"
        },
        "&:hover fieldset": {
            borderColor: "#6B9DFE"
        },
        "&.Mui-focused fieldset": {
            borderColor: "#979797",
            borderWidth: "1px"
        },
    },
    "& .MuiInputBase-input": {
        padding: "10px 14px",
        fontSize: "14px",
    },
};

const optionItemStyle = {
    padding: "8px 12px",
    color: "#333",
    fontSize: "14px",
    borderBottom: "1px solid #e0e0e0",
    cursor: "pointer",
    backgroundColor: "#FFF",
    "&:hover": {
        backgroundColor: "#F5F8FE"
    }
};

const checkboxStyle = {
    color: "#979797",
    "&.Mui-checked": {
        color: "#979797",
    },
    padding: "0 8px 0 0"
};

const autocompleteStyle = {
    cursor: "pointer",
    "& .MuiAutocomplete-popupIndicator": { color: "#707275" },
    "& .MuiAutocomplete-clearIndicator": { color: "#707275" },
};

const sliderStyle = {
    color: "#979797",
    height: 6,
    padding: "15px 0",
    "& .MuiSlider-thumb": {
        width: 16,
        height: 16,
        backgroundColor: "#979797",
        border: "2px solid #FFF",
        boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
    },
    "& .MuiSlider-track": {
        backgroundColor: "#979797",
    },
    "& .MuiSlider-rail": {
        background: "#c2ddd1",
    },
    "& .MuiSlider-mark": {
        backgroundColor: "#979797",
        width: 4,
        height: 4,
        borderRadius: "50%",
    },
    "& .MuiSlider-valueLabel": {
        backgroundColor: "#979797",
        color: "#FFF",
        fontSize: "0.75rem",
        padding: "2px 6px",
        borderRadius: "4px"
    }
};

export default Filters;